import { create } from 'zustand';

import { settingsRepo } from '../../data/repositories';
import { DEFAULT_SETTINGS } from '../../data/seed/defaults';
import { applyCssVars, applyTheme, settingsToCssVars } from './cssVars';
import type { Settings } from '../types';

/**
 * The one place runtime configuration lives (`specs/10-contracts.md`).
 *
 * The store boots with `DEFAULT_SETTINGS` and hydrates from IndexedDB afterwards, rather
 * than awaiting storage before first paint. That ordering is deliberate: nothing may sit
 * between the person and a keyboard, and `tokens.css` already carries the same defaults, so
 * the pre-hydration frame is correct rather than merely non-blocking.
 *
 * Every write goes through `update`, which persists and re-applies the CSS variables. A
 * second writer would mean a setting that looks changed and is not, which for geometry means
 * a caregiver adjusting key size and watching nothing happen.
 */
interface ProfileState {
  settings: Settings;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  update: (patch: Partial<Settings>) => Promise<void>;
}

function applyToDocument(settings: Settings): void {
  if (typeof document === 'undefined') return;
  applyCssVars(document.documentElement, settingsToCssVars(settings));
  applyTheme(document.documentElement, settings.theme);
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  hydrated: false,

  hydrate: async () => {
    try {
      const settings = await settingsRepo.get();
      applyToDocument(settings);
      set({ settings, hydrated: true });
    } catch (err) {
      // Defaults are already applied and usable, so a storage failure degrades to "the
      // caregiver's adjustments are missing", not "the app does not start".
      console.error('[profile] hydrate failed, staying on defaults', err);
      set({ hydrated: true });
    }
  },

  update: async (patch) => {
    const next = { ...get().settings, ...patch };
    // Applied before persisting: a geometry change must feel immediate (NFR2), and the
    // write is the slower half.
    applyToDocument(next);
    set({ settings: next });
    try {
      await settingsRepo.update(patch);
    } catch (err) {
      console.error('[profile] could not persist settings', err);
    }
  },
}));

/** Non-reactive read, for modules outside React. */
export const getSettings = (): Settings => useProfileStore.getState().settings;
